import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Phone,
  Plus,
  MoreVertical,
  Settings,
  Star,
  Trash2,
  Info,
  ExternalLink,
  PhoneForwarded,
  MessageSquare,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { BuyNumberModal, type AvailableNumber } from "@/components/phone/BuyNumberModal";
import {
  ConfigureNumberModal,
  type PhoneNumber,
  type TeamMember,
} from "@/components/phone/ConfigureNumberModal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Check if Twilio feature is enabled via environment variable
// const TWILIO_ENABLED = import.meta.env.VITE_TWILIO_ENABLED === "true";
const TWILIO_ENABLED = true;
// API types matching server responses
interface AgencyPhoneNumber {
  id: number;
  agencyId: string;
  twilioPhoneNumberSid: string;
  phoneNumber: string;
  friendlyName: string | null;
  numberType: string | null;
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  } | null;
  isDefault: boolean;
  smsEnabled: boolean;
  assignedToUserId: string | null;
  status: string;
  monthlyPrice: string | null;
  purchasedAt: string;
  createdAt: string;
  updatedAt: string;
}

interface TwilioSettings {
  provisioningStatus: string;
  isActive: boolean;
  twilioConfigured: boolean;
  twilioSubaccountSid?: string;
  provisioningError?: string;
  agencyName?: string;
}

// Convert API response to PhoneNumber type for modals
function toPhoneNumber(apiNumber: AgencyPhoneNumber): PhoneNumber {
  return {
    id: String(apiNumber.id),
    number: apiNumber.phoneNumber,
    friendlyName: apiNumber.friendlyName || "",
    type: (apiNumber.numberType as "local" | "toll-free") || "local",
    capabilities: apiNumber.capabilities || { voice: true, sms: true, mms: false },
    assignedTo: apiNumber.assignedToUserId
      ? { id: apiNumber.assignedToUserId, name: apiNumber.assignedToUserId }
      : undefined,
    isDefault: apiNumber.isDefault,
    monthlyPrice: apiNumber.monthlyPrice ? parseFloat(apiNumber.monthlyPrice) : 0,
    status: (apiNumber.status as "active" | "pending" | "suspended") || "active",
    smsEnabled: apiNumber.smsEnabled,
  };
}

// Mock team members (TODO: fetch from API)
const mockTeamMembers: TeamMember[] = [
  { id: "1", name: "John Smith", email: "john@agency.com" },
  { id: "2", name: "Jane Doe", email: "jane@agency.com" },
  { id: "3", name: "Bob Wilson", email: "bob@agency.com" },
  { id: "4", name: "Alice Brown", email: "alice@agency.com" },
];

export function PhoneSettingsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [configureModalOpen, setConfigureModalOpen] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState<PhoneNumber | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [numberToDelete, setNumberToDelete] = useState<AgencyPhoneNumber | null>(null);

  // Fetch Twilio settings
  const { data: twilioSettings, isLoading: isLoadingSettings } = useQuery<TwilioSettings>({
    queryKey: ["/api/agency/twilio-settings"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/agency/twilio-settings");
      return res.json();
    },
  });

  // Fetch phone numbers
  const { data: phoneNumbers = [], isLoading: isLoadingNumbers } = useQuery<AgencyPhoneNumber[]>({
    queryKey: ["/api/agency/phone-numbers"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/agency/phone-numbers");
      return res.json();
    },
    enabled: twilioSettings?.provisioningStatus === "provisioned",
  });

  // Provision Twilio sub-account
  const provisionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/agency/twilio-settings/provision");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/twilio-settings"] });
      toast({
        title: "Phone Service Enabled",
        description: "Your phone service has been set up successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to enable phone service",
        variant: "destructive",
      });
    },
  });

  // Purchase phone number
  const purchaseMutation = useMutation({
    mutationFn: async (data: {
      phoneNumber: string;
      friendlyName?: string;
      smsEnabled?: boolean;
      isDefault?: boolean;
      type?: "local" | "toll-free";
    }) => {
      const res = await apiRequest("POST", "/api/agency/phone-numbers/purchase", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/phone-numbers"] });
      toast({
        title: "Number Purchased",
        description: "The phone number has been added to your account.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to purchase phone number",
        variant: "destructive",
      });
    },
  });

  // Update phone number config
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PATCH", `/api/agency/phone-numbers/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/phone-numbers"] });
      toast({
        title: "Settings Saved",
        description: "Phone number configuration has been updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update phone number",
        variant: "destructive",
      });
    },
  });

  // Set default phone number
  const setDefaultMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/agency/phone-numbers/${id}/default`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/phone-numbers"] });
      toast({
        title: "Default Number Updated",
        description: "The default phone number has been changed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to set default number",
        variant: "destructive",
      });
    },
  });

  // Delete phone number
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/agency/phone-numbers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/phone-numbers"] });
      toast({
        title: "Number Removed",
        description: "The phone number has been released from your account.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete phone number",
        variant: "destructive",
      });
    },
  });

  const handlePurchaseNumber = (availableNumber: AvailableNumber) => {
    purchaseMutation.mutate({
      phoneNumber: availableNumber.number,
      type: availableNumber.type,
      isDefault: phoneNumbers.length === 0,
    });
  };

  const handleConfigureNumber = (number: AgencyPhoneNumber) => {
    setSelectedNumber(toPhoneNumber(number));
    setConfigureModalOpen(true);
  };

  const handleSaveConfiguration = (config: {
    friendlyName: string;
    assignedTo?: string;
    callRecording: boolean;
    recordingMessage?: string;
    smsEnabled?: boolean;
  }) => {
    if (!selectedNumber) return;

    updateMutation.mutate({
      id: parseInt(selectedNumber.id),
      data: {
        friendlyName: config.friendlyName,
        assignedToUserId: config.assignedTo || null,
        smsEnabled: config.smsEnabled,
      },
    });
  };

  const handleSetDefault = (number: AgencyPhoneNumber) => {
    setDefaultMutation.mutate(number.id);
  };

  const handleDeleteNumber = (number: AgencyPhoneNumber) => {
    setNumberToDelete(number);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteNumber = () => {
    if (!numberToDelete) return;
    deleteMutation.mutate(numberToDelete.id);
    setDeleteDialogOpen(false);
    setNumberToDelete(null);
  };

  const isLoading = isLoadingSettings || isLoadingNumbers;
  const isProvisioned = twilioSettings?.provisioningStatus === "provisioned";

  // Show "contact support" message if Twilio feature is disabled via env var
  if (!TWILIO_ENABLED) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Phone Numbers</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Purchase and manage phone numbers for your agency
            </p>
          </div>
        </div>

        <Alert variant="default">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Phone & SMS Not Available</AlertTitle>
          <AlertDescription>
            Phone and SMS services require additional configuration. Please contact support to enable this feature for your account.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show loading state when Twilio is enabled
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show provisioning prompt if not provisioned
  if (!isProvisioned) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Phone Numbers</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Purchase and manage phone numbers for your agency
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4">
                <Phone className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Enable Phone & SMS</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
                Set up phone service for your agency to purchase numbers and send SMS messages
              </p>
              {twilioSettings?.provisioningError && (
                <Alert variant="destructive" className="mb-4 max-w-md mx-auto">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{twilioSettings.provisioningError}</AlertDescription>
                </Alert>
              )}
              <Button
                onClick={() => provisionMutation.mutate()}
                disabled={provisionMutation.isPending}
              >
                {provisionMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Phone className="h-4 w-4 mr-2" />
                    Enable Phone Service
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Phone Numbers</h2>
            <Badge variant="secondary">
              {phoneNumbers.length} Phone Number{phoneNumbers.length !== 1 ? "s" : ""}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Purchase and manage phone numbers for your agency
          </p>
        </div>
        <Button onClick={() => setBuyModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Number
        </Button>
      </div>

      {/* Info Banner */}
      {/* <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            SMS messaging requires A2P 10DLC registration with your carrier.{" "}
            <a
              href="#"
              className="font-medium underline hover:no-underline inline-flex items-center gap-1"
            >
              View setup guide
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
      </div> */}

      {/* Phone Numbers List */}
      {phoneNumbers.length === 0 ? (
        /* Empty State */
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mx-auto mb-4">
                <Phone className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No phone numbers yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
                Purchase your first number to start making and receiving calls
              </p>
              <Button onClick={() => setBuyModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Number
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>SMS Marketing</TableHead>
                  <TableHead>Forwarding</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {phoneNumbers.map((number) => (
                  <TableRow key={number.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">
                          {number.phoneNumber}
                        </span>
                        {number.isDefault && (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                            Default
                          </Badge>
                        )}
                      </div>
                      {number.friendlyName && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {number.friendlyName}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {number.numberType === "toll-free" ? "Toll Free" : "Local"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {number.smsEnabled ? (
                        <Badge variant="default" className="gap-1 bg-green-100 text-green-800 hover:bg-green-100">
                          <MessageSquare className="h-3 w-3" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <MessageSquare className="h-3 w-3" />
                          Disabled
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1.5 text-muted-foreground cursor-default">
                            <PhoneForwarded className="h-4 w-4" />
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              Coming Soon
                            </Badge>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Call forwarding configuration coming soon</p>
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleConfigureNumber(number)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {!number.isDefault && (
                            <DropdownMenuItem onClick={() => handleSetDefault(number)}>
                              <Star className="h-4 w-4 mr-2" />
                              Set as Default
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDeleteNumber(number)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Buy Number Modal */}
      <BuyNumberModal
        open={buyModalOpen}
        onOpenChange={setBuyModalOpen}
        onPurchase={handlePurchaseNumber}
      />

      {/* Configure Number Modal */}
      <ConfigureNumberModal
        open={configureModalOpen}
        onOpenChange={setConfigureModalOpen}
        phoneNumber={selectedNumber}
        teamMembers={mockTeamMembers}
        onSave={handleSaveConfiguration}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Phone Number</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {numberToDelete?.phoneNumber}? This action cannot be
              undone and the number will be released back to the pool.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteNumber}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Number
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
